import React from "react";
import { ArrowRight, Quote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Reveal from "../components/Reveal";
import { PageHero } from "./Services";
import { caseStudies, testimonials, company } from "../data/mock";
import Seo from "../components/Seo";

const CaseStudies = () => {
  return (
    <div className="overflow-hidden">
      <Seo title="Case Studies — Real Client Results" description="See how Jellis Enterprise grows local businesses with measurable results in leads, revenue, and search rankings — not vanity metrics." />
      <PageHero
        eyebrow="Case Studies"
        title="Real businesses. Real growth."
        subtitle="We measure success in leads, revenue, and rankings — not vanity metrics. Here’s what that looks like in practice."
      />

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6 space-y-10">
          {caseStudies.map((c, i) => (
            <Reveal key={c.client} delay={i * 60}>
              <div className={`grid lg:grid-cols-5 gap-8 items-center rounded-3xl border border-slate-200 overflow-hidden bg-white card-lift`}>
                <div className={`lg:col-span-2 relative h-64 lg:h-full min-h-[280px] ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <img src={c.image} alt={c.client} className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-white/90 text-blue-700 hover:bg-white font-semibold">{c.industry}</Badge>
                </div>
                <div className="lg:col-span-3 p-8 md:p-10">
                  <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">{c.client}</h2>
                  <p className="mt-3 text-lg text-slate-600 leading-relaxed">{c.summary}</p>
                  <div className="mt-8 grid grid-cols-3 gap-4">
                    {c.metrics.map((m) => (
                      <div key={m.label} className="rounded-xl bg-slate-50 border border-slate-100 p-4 text-center">
                        <p className="font-display text-2xl md:text-3xl font-extrabold text-gradient">{m.value}</p>
                        <p className="text-xs text-slate-500 mt-1">{m.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured quote */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <Quote className="mx-auto h-12 w-12 text-blue-200" />
            <p className="mt-6 font-display text-2xl md:text-3xl font-bold text-slate-900 leading-snug">
              “{testimonials[0].quote}”
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full brand-gradient text-white font-bold">
                {testimonials[0].initials}
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">{testimonials[0].name}</p>
                <p className="text-sm text-slate-500">{testimonials[0].role}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl navy-gradient p-10 md:p-16 text-center">
              <div className="blob h-64 w-64 bg-blue-500 -top-10 left-1/3" />
              <div className="relative z-10">
                <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white">Ready to be our next success story?</h2>
                <p className="mt-4 text-lg text-blue-100/80 max-w-2xl mx-auto">
                  Book a free strategy call and we’ll show you exactly how we’d grow your business.
                </p>
                <a href={company.bookingUrl} target="_blank" rel="noreferrer">
                  <Button size="lg" className="mt-8 bg-white text-blue-700 hover:bg-blue-50 font-bold text-base group">
                    Schedule a Call Today
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
